import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDateTime, formatNumber, MESSAGE_THREAD_STATUS } from '@blimo/shared';
import { Checkbox, DataTable, ErrorState, Label, PageHeader, SearchInput, StatusBadge } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../../components/filter-bar.jsx';
import { PropertyFilter } from '../../finance/panels.jsx';
import { useListState } from '../../../lib/list-search.js';

const STATUS_OPTIONS = Object.entries(MESSAGE_THREAD_STATUS).map(([value, meta]) => ({ value, label: meta.label }));

/** Kommunikation › Posteingang: Nachrichten der Eigentümer (F-504). */
export function InboxListPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = {
    ...list.apiParams,
    sort: undefined,
    filter: { search: search.q, status: search.status, property_id: search.property, assigned_to_me: search.mine === '1' ? true : undefined },
  };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.messages(api, params));
  const columns = useMemo(() => threadColumns(), []);
  const open = data?.meta?.open;

  return (
    <>
      <PageHeader
        title="Posteingang"
        description={open != null ? `Nachrichten der Eigentümer · ${formatNumber(open)} offen insgesamt` : 'Nachrichten der Eigentümer an die Verwaltung.'}
      />
      <FilterBar>
        <SearchInput label="Nachrichten durchsuchen" placeholder="Betreff …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} />
        <PropertyFilter value={search.property} onChange={(v) => list.setFilter('property', v)} />
        <div className="flex h-(--control-height-sm) items-center gap-2">
          <Checkbox id="inbox-mine" checked={search.mine === '1'} onChange={(e) => list.setFilter('mine', e.target.checked ? '1' : undefined)} />
          <Label htmlFor="inbox-mine" className="font-normal">
            Nur mir zugewiesene
          </Label>
        </div>
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Nachrichten"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Nachrichten gefunden"
        />
      )}
    </>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Spalten werden im Liegenschafts-Tab wiederverwendet
export function threadColumns() {
  return [
    {
      id: 'subject',
      header: 'Betreff',
      cell: ({ row }) => (
        <Link to="/kommunikation/posteingang/$threadId" params={{ threadId: String(row.original.id) }} className="font-medium text-primary hover:underline">
          {row.original.subject}
        </Link>
      ),
    },
    {
      id: 'object',
      header: 'Liegenschaft / Einheit',
      cell: ({ row }) => (
        <span className="flex flex-col text-sm">
          <span>{row.original.property?.name ?? '–'}</span>
          {row.original.unit && (
            <span className="text-xs text-muted-foreground">{[row.original.unit.number, row.original.unit.designation].filter(Boolean).join(' · ')}</span>
          )}
        </span>
      ),
    },
    { id: 'creator', header: 'Von', cell: ({ row }) => row.original.creator?.name ?? '–' },
    { id: 'status', header: 'Status', cell: ({ row }) => <StatusBadge domain="messageThread" status={row.original.status} /> },
    { id: 'assignee', header: 'Zuständig', cell: ({ row }) => row.original.assignee?.name ?? <span className="text-muted-foreground">–</span> },
    { id: 'last', header: 'Letzte Nachricht', cell: ({ row }) => <span className="numeric">{formatDateTime(row.original.last_message_at)}</span> },
  ];
}
